import { useState, useCallback, useEffect } from 'react';

export interface WirelessPrinterState {
  isConnected: boolean;
  isPrinting: boolean;
  pairedDevice: string | null;
  error: string | null;
}

const GATT_SERVICE_UUID = '000018f0-0000-1000-8000-00805f9b34fb';

export function useWirelessPrinter() {
  const [state, setState] = useState<WirelessPrinterState>({
    isConnected: false,
    isPrinting: false,
    pairedDevice: null,
    error: null,
  });

  const [gattServer, setGattServer] = useState<any>(null);
  const [writeCharacteristic, setWriteCharacteristic] = useState<any>(null);

  const handleDisconnect = useCallback(() => {
    setState({
      isConnected: false,
      isPrinting: false,
      pairedDevice: null,
      error: 'Wireless printer disconnected.',
    });
    setGattServer(null);
    setWriteCharacteristic(null);
  }, []);

  // Monitor disconnection event
  useEffect(() => {
    if (gattServer?.device) {
      const device = gattServer.device;
      device.addEventListener('gattserverdisconnected', handleDisconnect);
      return () => {
        device.removeEventListener('gattserverdisconnected', handleDisconnect);
      };
    }
  }, [gattServer, handleDisconnect]);

  const connectPrinter = async () => {
    setState((s) => ({ ...s, error: null, isPrinting: false }));

    const nav = navigator as any;
    if (!nav.bluetooth) {
      setState((s) => ({
        ...s,
        error:
          'Web Bluetooth is not supported in this browser. Please use Chrome, Edge, or Opera on HTTPS.',
      }));
      return false;
    }

    try {
      const device = await nav.bluetooth.requestDevice({
        filters: [
          { services: [GATT_SERVICE_UUID] },
          { namePrefix: 'Tej' },
          { namePrefix: 'Printer' },
          { namePrefix: 'MTP' },
          { namePrefix: 'EP58' },
        ],
        optionalServices: [
          GATT_SERVICE_UUID,
          '000018f0-0000-1000-8000-00805f9b34fb',
          '00001101-0000-1000-8000-00805f9b34fb',
        ],
      });

      setState((s) => ({ ...s, pairedDevice: device.name || 'Wireless Printer' }));

      const server = await device.gatt.connect();
      setGattServer(server);

      // Get primary service
      const service = await server.getPrimaryService(GATT_SERVICE_UUID).catch(async () => {
        // Fallback to searching all services if exact UUID matching is restricted
        const services = await server.getPrimaryServices();
        if (services.length > 0) return services[0];
        throw new Error('No Bluetooth services found on the printer.');
      });

      // Find write characteristic
      const characteristics = await service.getCharacteristics();
      const writeChar = characteristics.find(
        (char: any) => char.properties.write || char.properties.writeWithoutResponse,
      );

      if (!writeChar) {
        throw new Error('Could not find a write characteristic on the printer service.');
      }

      setWriteCharacteristic(writeChar);
      setState((s) => ({ ...s, isConnected: true, error: null }));
      return true;
    } catch (err: any) {
      console.error('Wireless Bluetooth printer connection error:', err);
      setState((s) => ({
        ...s,
        isConnected: false,
        pairedDevice: null,
        error: err.message || 'Failed to establish connection with printer.',
      }));
      return false;
    }
  };

  const disconnectPrinter = async () => {
    if (gattServer) {
      try {
        await gattServer.disconnect();
      } catch (err) {
        console.error('Failed to disconnect cleanly:', err);
      }
    }
    handleDisconnect();
  };

  const sendPrintJob = async (byteData: Uint8Array): Promise<boolean> => {
    if (!writeCharacteristic) {
      setState((s) => ({ ...s, error: 'Printer is not connected.' }));
      return false;
    }

    setState((s) => ({ ...s, isPrinting: true, error: null }));

    try {
      // Send data in 20-byte packet chunks to avoid BLE characteristic buffer constraints
      const chunkSize = 20;
      for (let i = 0; i < byteData.length; i += chunkSize) {
        const chunk = byteData.slice(i, i + chunkSize);

        // Use writeValueWithResponse or writeValueWithoutResponse based on support
        if (writeCharacteristic.writeValueWithoutResponse) {
          await writeCharacteristic.writeValueWithoutResponse(chunk);
        } else {
          await writeCharacteristic.writeValue(chunk);
        }

        // Add a micro delay (15ms) to give the thermal printer buffer time to process
        await new Promise((resolve) => setTimeout(resolve, 15));
      }

      setState((s) => ({ ...s, isPrinting: false }));
      return true;
    } catch (err: any) {
      console.error('Wireless Printer Write Error:', err);
      setState((s) => ({
        ...s,
        isPrinting: false,
        error: `Printing failed: ${err.message || 'Bluetooth transmission error'}`,
      }));
      return false;
    }
  };

  return {
    isConnected: state.isConnected,
    isPrinting: state.isPrinting,
    pairedDevice: state.pairedDevice,
    error: state.error,
    connectPrinter,
    disconnectPrinter,
    sendPrintJob,
  };
}
